import React from "react";
import { css, Theme, useTheme } from "@emotion/react";
import { Interpolation } from "@emotion/serialize/dist/emotion-serialize.cjs";
import Code from "../code/Code";

export interface TypographyProps {
  text: string;
  size?: "xs" | "sm" | "md" | "lg" | "xl" | "xxl";
  variant?: "display" | "text";
  weight?: "regular" | "medium" | "semibold" | "bold";
  color?: string;
  extraCss?: Interpolation<Theme>;
  removeLineHeight?: boolean;
  centerAlign?: boolean;
  ignoreSelect?: boolean;
  wrap?: boolean;
}

const parseTextWithCode = (text: string) => {
  // Split text by <code> tags.
  const splitText = text.split(/<code>|<\/code>/g);

  return splitText.map((segment, index) => {
    // Even indexes will always be regular text, odd ones are code.
    if (index % 2 === 0) {
      return segment;
    } else {
      // Return a styled span for the code.
      return <Code text={segment} key={index} />;
    }
  });
};

const Typography: React.FC<TypographyProps> = (props) => {
  const theme = useTheme();

  const size = props.size || "md";
  const variant = props.variant || "text";
  const weight = props.weight || "regular";
  const color = props.color || theme.colours.grey[900];

  const style = css({
    userSelect: props.ignoreSelect ? "none" : undefined,
    fontFamily: theme.fonts.family.body,
    fontSize: theme.fonts.size[variant][size],
    fontWeight: theme.fonts.weight[weight],
    letterSpacing: theme.fonts.letterSpacing[variant][size],
    lineHeight: props.removeLineHeight
      ? undefined
      : `${theme.fonts.lineHeight[variant][size]}px`,
    color,
    margin: 0,
    padding: 0,
    textAlign: props.centerAlign ? "center" : undefined,
    textDecoration: "none",
    whiteSpace: props.wrap ? "normal" : "unset",
    wordBreak: "keep-all",
  });

  return (
    <p css={[style, props.extraCss]}>{parseTextWithCode(props.text ?? "")}</p>
  );
};

export default Typography;
